import { AssetFormRepository } from 'services/repositories'
import LandingPagesRepository from 'services/repositories/LandingPagesRepository'
import { FormField } from 'types/form-fields'

interface AssetFormFieldNames {
  assetTypeId: 'assetTypeId'
  country: 'country'
  name: 'name'
  additionalText?: 'additionalText'
  image?: 'image'
  largeImage?: 'largeImage'
  landingPageFile?: 'landingPageFile'
  sourceGraphicFile?: 'sourceGraphicFile'
  descriptionForEuro?: 'descriptionForEuro'
  defaultDescription?: 'defaultDescription'
  leaveImage?: 'leaveImage'
  leaveLargeImage?: 'leaveLargeImage'
  leaveSourceGraphicFile?: 'leaveSourceGraphicFile'
  pdfFile?: 'pdfFile'
  leavePdfFile?: 'leavePdfFile'
  attributeAlt?: 'attributeAlt'
  activeImage?: 'activeImage'
  activeImageAlt?: 'activeImageAlt'
  attributeAltMobile?: 'attributeAltMobile'
  bannerTitle?: 'bannerTitle'
  bannerTitleAccent?: 'bannerTitleAccent'
  bannerSubtitle?: 'bannerSubtitle'
  bannerDisclaimer?: 'bannerDisclaimer'
  videoTitle?: 'videoTitle'
  videoUrl?: 'videoUrl'
  videoYoutubeId?: 'videoYoutubeId'
  video?: 'video'
  packshot?: 'packshot'
  openedPackshot?: 'openedPackshot'
  packshotAlt?: 'packshotAlt'
  openedPackshotAlt?: 'openedPackshotAlt'
  sku?: 'sku'
  leavePackshot?: 'leavePackshot'
  leaveOpenedPackshot?: 'leaveOpenedPackshot'
}

export class AssetFormService {
  assetFormFieldNames: AssetFormFieldNames = {
    assetTypeId: 'assetTypeId',
    country: 'country',
    name: 'name',
    // @agata - additionalText is obsolete, should be removed in the future
    additionalText: 'additionalText',
    image: 'image',
    // @agata - largeImage works as image for mobile view (api didn't renamed it)
    largeImage: 'largeImage',
    landingPageFile: 'landingPageFile',
    sourceGraphicFile: 'sourceGraphicFile',
    descriptionForEuro: 'descriptionForEuro',
    defaultDescription: 'defaultDescription',
    attributeAlt: 'attributeAlt',
    activeImage: 'activeImage',
    activeImageAlt: 'activeImageAlt',
    attributeAltMobile: 'attributeAltMobile',
    bannerTitle: 'bannerTitle',
    bannerTitleAccent: 'bannerTitleAccent',
    bannerSubtitle: 'bannerSubtitle',
    bannerDisclaimer: 'bannerDisclaimer',
    pdfFile: 'pdfFile',
    leavePdfFile: 'leavePdfFile',
    videoTitle: 'videoTitle',
    videoUrl: 'videoUrl',
    videoYoutubeId: 'videoYoutubeId',
    video: 'video',
    packshot: 'packshot',
    openedPackshot: 'openedPackshot',
    packshotAlt: 'packshotAlt',
    openedPackshotAlt: 'openedPackshotAlt',
    sku: 'sku',
    leavePackshot: 'leavePackshot',
    leaveOpenedPackshot: 'leaveOpenedPackshot'
  }

  requiredFields = [
    this.assetFormFieldNames.name,
    this.assetFormFieldNames.assetTypeId,
    this.assetFormFieldNames.country
  ]

  /** LP "Logo" — desktop `image` + mobile `largeImage` (each with alt). AI maps those alts. */
  private lpLogoTypeDef() {
    return {
      name: 'Logo',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.attributeAltMobile
      ]
    }
  }

  assetTypes = {
    banner: {
      name: 'Banner',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.landingPageFile!,
        this.assetFormFieldNames.attributeAltMobile,
        this.assetFormFieldNames.bannerTitle!,
        this.assetFormFieldNames.bannerTitleAccent!,
        this.assetFormFieldNames.bannerSubtitle!,
        this.assetFormFieldNames.additionalText!,
        this.assetFormFieldNames.bannerDisclaimer!
      ]
    },
    logo: this.lpLogoTypeDef(),
    first_lead: {
      name: 'First Lead',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.descriptionForEuro
      ]
    },
    features: {
      name: 'Features',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt
      ]
    },
    intro: {
      name: 'Intro',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.additionalText,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.descriptionForEuro,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.videoYoutubeId!
      ]
    },
    icons: {
      name: 'Icons',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.activeImage!,
        this.assetFormFieldNames.activeImageAlt!,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.descriptionForEuro,
        this.assetFormFieldNames.attributeAlt
      ]
    },
    second_lead: {
      name: 'Second Lead',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.descriptionForEuro
      ]
    },
    functions: {
      name: 'Functions',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.descriptionForEuro,
        this.assetFormFieldNames.additionalText,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.landingPageFile!,
        this.assetFormFieldNames.attributeAltMobile
      ]
    },
    footer: {
      name: 'Footer',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.landingPageFile!,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.attributeAltMobile
      ]
    },
    backgrounds: {
      name: 'Backgrounds',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.attributeAltMobile
      ]
    },
    photos: {
      name: 'Photos',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.attributeAltMobile
      ]
    },
    navigation: {
      name: 'Navigation',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt
      ]
    },
    // @agata - accessories are only for Hoover Vacuums
    accessories: {
      name: 'Accessories',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile
      ]
    },
    // Landing page asset type: section with one or more download links (PDF + button label)
    download_button: {
      name: 'Download Button',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.pdfFile!
      ]
    },
    // Landing page asset type: CTA link (button label + href target / LP comment)
    link_button: {
      name: 'Link Button',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.additionalText!
      ]
    },
    // Landing page asset type: placeholder box for external content (no file)
    product_box: {
      name: 'Product Box',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    // Landing page asset type: section lead – same fields as product First Lead
    lead: {
      name: 'Lead',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.descriptionForEuro,
        this.assetFormFieldNames.bannerTitleAccent!,
        this.assetFormFieldNames.additionalText
      ]
    },
    // Image asset (same fields as Photos) — used for Haier product slider slots
    slider: {
      name: 'Slider',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt
      ]
    },
    video: {
      name: 'Video',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.videoTitle!,
        this.assetFormFieldNames.videoYoutubeId!,
        this.assetFormFieldNames.videoUrl!,
        this.assetFormFieldNames.video!
      ]
    },
    packshot: {
      name: 'Packshot',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.sku!,
        this.assetFormFieldNames.packshot!,
        this.assetFormFieldNames.packshotAlt!,
        this.assetFormFieldNames.openedPackshot!,
        this.assetFormFieldNames.openedPackshotAlt!
      ]
    }
  }

  schneiderAssetTypes = {
    banner: {
      name: 'Banner',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.landingPageFile!,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.attributeAltMobile,
        this.assetFormFieldNames.bannerTitle!,
        this.assetFormFieldNames.bannerTitleAccent!,
        this.assetFormFieldNames.bannerSubtitle!,
        this.assetFormFieldNames.additionalText!,
        this.assetFormFieldNames.bannerDisclaimer!
      ]
    },
    logo: this.lpLogoTypeDef(),
    first_lead: {
      name: 'First Lead',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    icons: {
      name: 'Icons',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.activeImage!,
        this.assetFormFieldNames.activeImageAlt!,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    second_lead: {
      name: 'Second Lead',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    functions: {
      name: 'Functions',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.landingPageFile!,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    footer: {
      name: 'Footer',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.landingPageFile!
      ]
    },
    backgrounds: {
      name: 'Backgrounds',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage
      ]
    },
    photos: {
      name: 'Photos',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.attributeAltMobile
      ]
    },
    navigation: {
      name: 'Navigation',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile,
        this.assetFormFieldNames.attributeAlt
      ]
    },
    // @agata - accessories are only for Hoover Vacuums
    accessories: {
      name: 'Accessories',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.sourceGraphicFile
      ]
    },
    // Landing page asset type (Hotpoints) – used by LP asset add/edit
    hotpoints: {
      name: 'Hotpoints',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.additionalText,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.largeImage,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.attributeAltMobile
      ]
    },
    // Landing page asset type (Icon) – Hotpoints section callout image + label
    cursor: {
      name: 'Icon',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    icon: {
      name: 'Icon',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.image,
        this.assetFormFieldNames.attributeAlt,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    // Landing page asset type: section with one or more download links (PDF + button label)
    download_button: {
      name: 'Download Button',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.pdfFile!
      ]
    },
    // Landing page asset type: CTA link (button label + href target / LP comment)
    link_button: {
      name: 'Link Button',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription,
        this.assetFormFieldNames.additionalText!
      ]
    },
    // Landing page asset type: placeholder box for external content (no file)
    product_box: {
      name: 'Product Box',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.defaultDescription
      ]
    },
    packshot: {
      name: 'Packshot',
      allowedFields: [
        ...this.requiredFields,
        this.assetFormFieldNames.sku!,
        this.assetFormFieldNames.packshot!,
        this.assetFormFieldNames.packshotAlt!,
        this.assetFormFieldNames.openedPackshot!,
        this.assetFormFieldNames.openedPackshotAlt!
      ]
    }
  }

  getAssets(brand?: string): any {
    switch (brand) {
      case 'FD':
      case 'PP':
      case 'IA':
        return this.schneiderAssetTypes
    }

    return this.assetTypes
  }

  isFieldAllowedInAssetType = (
    assetType: string,
    fieldName: string,
    brand?: string
  ) => {
    const brandOrDefaultAssets = this.getAssets(brand)
    const normalized =
      String(assetType ?? '')
        .trim()
        .replace(/\s+/g, '_')
        .toLowerCase() || ''
    // Backward compatibility: treat "Text Banner" as "Banner".
    const normalizedKey =
      normalized === 'text_banner' || normalized === 'text-banner'
        ? 'banner'
        : normalized
    const key = normalizedKey as keyof typeof brandOrDefaultAssets
    let assetTypeConfig = brandOrDefaultAssets[key]

    // Product "Icons" assets are sometimes named "Icon" (singular) — prefer plural config
    if (
      !assetTypeConfig?.allowedFields &&
      normalized === 'icon' &&
      brandOrDefaultAssets.icons?.allowedFields
    ) {
      assetTypeConfig = brandOrDefaultAssets.icons
    }

    // Fallback: legacy Cursor / Action Icon map to Icon form; Icon / Icons use icon config
    if (
      !assetTypeConfig?.allowedFields &&
      (normalized.includes('cursor') ||
        normalized === 'icon' ||
        normalized === 'icons' ||
        normalized === 'action_icon')
    ) {
      assetTypeConfig =
        normalized === 'icon' ||
        normalized === 'icons' ||
        normalized === 'action_icon'
          ? brandOrDefaultAssets.icon ?? this.schneiderAssetTypes.icon
          : this.schneiderAssetTypes.cursor
    }

    // Fallback: Download Button may come from API as "Download Button"
    if (!assetTypeConfig?.allowedFields && normalized === 'download_button') {
      assetTypeConfig = this.schneiderAssetTypes.download_button
    }

    // Fallback: Product Box may come from API as "Product Box"
    if (!assetTypeConfig?.allowedFields && normalized === 'product_box') {
      assetTypeConfig = this.schneiderAssetTypes.product_box
    }

    if (!assetTypeConfig?.allowedFields && normalized === 'lead') {
      assetTypeConfig = this.assetTypes.lead
    }

    if (!assetTypeConfig?.allowedFields && normalized === 'slider') {
      assetTypeConfig = this.assetTypes.slider
    }

    if (!assetTypeConfig?.allowedFields && normalized === 'video') {
      assetTypeConfig = this.assetTypes.video
    }

    if (!assetTypeConfig?.allowedFields && normalized === 'packshot') {
      assetTypeConfig = this.assetTypes.packshot
    }

    if (
      !assetTypeConfig?.allowedFields &&
      (normalized === 'packshots' || normalized === 'pack_shot')
    ) {
      assetTypeConfig = this.assetTypes.packshot
    }

    if (
      !assetTypeConfig?.allowedFields &&
      normalized === 'photo' &&
      brandOrDefaultAssets.photos?.allowedFields
    ) {
      assetTypeConfig = brandOrDefaultAssets.photos
    }

    if (!assetTypeConfig?.allowedFields && normalized === 'logo') {
      assetTypeConfig = brandOrDefaultAssets.logo
    }

    if (!assetTypeConfig?.allowedFields) {
      return false
    }

    return assetTypeConfig.allowedFields.includes(fieldName)
  }

  async getBrandSpecificFields(
    brandGuid: string,
    assetType: string,
    lang: string
  ): Promise<FormField[]> {
    return (
      (await AssetFormRepository.getAssetFroms(
        brandGuid,
        assetType,
        lang
      ))
    ).data.formFields
  }

  /** Landing page asset forms: use by-brand-and-type API (not product asset-forms). */
  async getLandingPageBrandSpecificFields(
    brandId: string,
    landingPageAssetTypeId: string,
    lang: string
  ): Promise<FormField[]> {
    const langParam = lang === '' || lang === 'default' ? 'default' : lang
    const res =
      await LandingPagesRepository.getLandingPageAssetFormsByBrandAndType({
        brandId,
        landingPageAssetTypeId,
        lang: langParam
      })
    const body = (res as any)?.data ?? res
    const formFields = body?.formFields ?? body?.data?.formFields ?? []

    type RawFormField = {
      id: string
      fieldName: string
      label: string
      required?: boolean
    }
    const list: RawFormField[] = Array.isArray(formFields)
      ? (formFields as RawFormField[])
      : []

    return list.map(f => ({
      id: f.id,
      name: f.fieldName,
      field: f.fieldName,
      type: 'text',
      label: f.label ?? f.fieldName,
      required: !!f.required
    }))
  }
}

export const AssetFormServiceInstance = new AssetFormService()
